import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { task } from "ember-concurrency";
import { t } from "ember-intl";
import { and, not } from "ember-truth-helpers";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import canManage from "glesys-controlpanel/helpers/can-manage";
import modal from "glesys-controlpanel/helpers/modal";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntro from "glesys-controlpanel/components/page-content/service-intro";
import GButton from "glesys-controlpanel/components/g-button";
import DropdownItem from "glesys-controlpanel/components/dropdown/item";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import Entity from "glesys-controlpanel/components/entity";
import EntityList from "glesys-controlpanel/components/entity-list";
import EmptyState from "glesys-controlpanel/components/empty-state";
import { array } from "@ember/helper";
import IconsSidebarNetworking from "glesys-controlpanel/components/icons/sidebar/networking";

export default class PrivateNetworksOverview extends Component {
  @service router;
  @service store;

  get project() {
    return this.args.model.project;
  }

  get privateNetworks() {
    return this.args.model.privateNetworks;
  }

  createPrivateNetworkTask = task(async (name) => {
    let properties = {
      project: this.project,
      name,
    };

    let privateNetwork = this.store.createRecord("private-network", properties);
    await this.store.request(saveRecord(privateNetwork));

    this.router.transitionTo("private-networks.details", privateNetwork.id);
  });

  <template>
    <PageContent>
      <PageContentServiceIntro @title={{t "private-networks.title"}}>
        {{#if (and (canManage) this.privateNetworks)}}
          <GButton
            data-test-create-private-network-modal
            @color="green"
            {{on "click" (modal "modals/private-network-create" task=this.createPrivateNetworkTask)}}
          >
            {{t "label.create"}}
          </GButton>
        {{/if}}
      </PageContentServiceIntro>
      <EntityList @items={{this.privateNetworks}}>
        <:default as |privateNetwork|>
          <Entity
            @route="private-networks.details"
            @model={{privateNetwork.id}}
            @status={{privateNetwork.statusIndicator}}
            @title={{privateNetwork.name}}
            @key={{privateNetwork.id}}
            @linkLabel={{t "private-networks.manage-network" name=privateNetwork.name}}
            @hideActions={{not (canManage)}}
          >
            <:actions>
              <DropdownItem
                data-test-private-network-edit
                {{on
                  "click"
                  (modal "modals/private-network-edit" privateNetwork=privateNetwork task=privateNetwork.updateTask)
                }}
              >
                {{t "label.edit"}}
              </DropdownItem>
              <DropdownDestructiveItem
                data-test-private-network-delete
                {{on
                  "click"
                  (modal
                    "modals/organism-delete"
                    entity=privateNetwork.name
                    task=privateNetwork.deletePrivateNetworkTask
                    entityKey=privateNetwork.id
                  )
                }}
              >
                {{t "button.delete"}}
              </DropdownDestructiveItem>
            </:actions>
          </Entity>
        </:default>
        <:empty>
          <EmptyState
            @icon={{IconsSidebarNetworking}}
            @title={{t "private-networks.empty-state.title"}}
            @description={{t "private-networks.empty-state.description"}}
            @points={{array
              (t "private-networks.empty-state.usp-list.off-public-internet")
              (t "private-networks.empty-state.usp-list.kvm-and-vmware")
              (t "private-networks.empty-state.usp-list.segments")
            }}
            @learnMoreUrl="https://docs.glesys.com/products/compute/vmware-virtual-machines/how-tos/manage-private-networks"
            @learnMoreAriaLabel={{t "private-networks.empty-state.learn-more-aria-label"}}
            @createLabel={{t "private-networks.empty-state.button-create"}}
            @onCreate={{if (canManage) (modal "modals/private-network-create" task=this.createPrivateNetworkTask)}}
          />
        </:empty>
      </EntityList>
    </PageContent>
  </template>
}
